import { UserResponse } from 'compliance-api';
import capitalize from 'lodash/capitalize';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '@/modules/common/components/Badge.tsx';
import { DeleteModal } from '@/modules/admin-dashboard/components/modals/DeleteModal.tsx';
import { useDeleteUsersFromBusinessMutation } from '@/modules/admin-dashboard/queries/users.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { Avatar, DisclosureListItem, DropdownMenu, ModalProviderContext } from 'ui';
import { DotsHorizontalIcon } from 'ui/icons';
import { useResendEmailsMutation } from '@/modules/admin-dashboard/queries/tasks.ts';

interface UserManagementItemProps {
  user: UserResponse;
}

const UserManagementItem = ({ user }: UserManagementItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const navigate = useNavigate();

  const { data } = useCurrentUserQuery();

  const handleOpenDeleteUserModal = useCallback(
    (user: { id: number; first_name: string; last_name: string }) => {
      openModal(DeleteModal, {
        name: user?.first_name + ' ' + user?.last_name || '',
        id: user?.id || 0,
        mutation: useDeleteUsersFromBusinessMutation,
        title: 'User',
      });
    },
    [openModal],
  );
  const { mutateAsync } = useResendEmailsMutation();

  const handleSendInviteLinkModal = useCallback(
    async (user: { id: number }) => {
      try {
        await mutateAsync({
          userId: user.id
        });
      } catch (error) {
        console.error('Failed to send invite link:', error);
      }
    },
    [mutateAsync],
  );

  const dropdownOptions = useMemo(() => {
    const res = [
      {
        label: 'Edit Departments',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.departments,
            search: location.search,
          }),
      },
    ];

    const isCurrentUser = user.id === data?.user?.id || 0;

    if (!isCurrentUser) {
      res.push({
        label: 'Delete',
        onClick: () => handleOpenDeleteUserModal(user),
      });
    }

    if (!isCurrentUser) {
      res.push({
        label: 'Resend invite link',
        onClick: () => handleSendInviteLinkModal(user),
      });
    }

    return res;
  }, [data?.user?.id, handleOpenDeleteUserModal, navigate, user, handleSendInviteLinkModal]);

  const leftSubduedContentSet = useMemo(() => {
    const roles = (user?.roles || []).map(capitalize).join(', ');
    return [user.email, roles];
  }, [user.email, user?.roles]);
  return (
    <DisclosureListItem
      leftContent={
        <Avatar firstName={user.first_name} lastName={user.last_name} src={user?.avatar_url} />
      }
      leftMainString={`${user.first_name} ${user.last_name}`}
      leftSubduedContentSet={leftSubduedContentSet}
      rightLeftContent={
        <div className="flex flex-1 justify-end flex-wrap gap-3 pr-4">
          {(user.department || [])
            .sort((a, b) => a.name.length - b.name.length)
            .map((value, index) => (
              <Badge type="info" key={index}>
                <span className="truncate overflow-ellipsis">{value.name}</span>
              </Badge>
            ))}
        </div>
      }
      rightContent={
        <DropdownMenu
          autoPlace
          items={dropdownOptions}
          button={
            <button className="flex items-center justify-center">
              <img src={DotsHorizontalIcon} alt="Loading..." />
            </button>
          }
          placement="left-start"
          className="w-[180px] divide-white"
        />
      }
    />
  );
};

export default UserManagementItem;
